import { apiBase } from './apiBase';
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import {
  periodKey,
  periodParams,
  type ReportCompareMode,
  type ReportPeriodKind,
  type ReportPeriodState,
} from './reportPeriod';

// --- Types (mirror backend src/modules/financials/financials.monthlyReport.ts
// --- and financials.monthlyReportClosing.ts) ----------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

export interface MoneyByCurrency {
  currency: string;
  amount: number;
}

export interface MonthTotals {
  inflows: number;
  outflows: number;
  net: number;
}

/**
 * Versiunea formei raportului, ținută în sincron cu `REPORT_VERSION` din
 * `backend/src/modules/financials/financials.monthlyReport.ts`: un snapshot
 * închis cu o versiune mai mică nu are secțiunile adăugate între timp.
 */
export const REPORT_VERSION = 2;

export interface ReportPeriodInfo {
  kind: ReportPeriodKind;
  key: string;
  from: string;
  to: string;
  months: Array<{ year: number; month: number }>;
  monthsCount: number;
  compare: { mode: ReportCompareMode; key: string };
}

export interface ReportSummary extends MonthTotals {
  currency: 'RON';
  previous: MonthTotals | null;
  change: { inflows: number | null; outflows: number | null; net: number | null };
  bankBalance: number | null;
  bankBalanceAsOf: string | null;
  bankBalanceChange: number | null;
  invoiced: number;
  collected: number;
  otherCurrencies: { inflows: MoneyByCurrency[]; outflows: MoneyByCurrency[] };
  /** „Finanțare și distribuiri” (RON): not in the inflows / outflows above. */
  financing?: {
    inflows: number;
    outflows: number;
    byCategory: { code: string; label: string; inflows: number; outflows: number }[];
    otherCurrencies?: { inflows: MoneyByCurrency[]; outflows: MoneyByCurrency[] };
  };
}

export interface AccountMonth {
  key: string;
  bankName: string;
  label: string | null;
  iban: string | null;
  currency: string;
  openingBalance: number | null;
  closingBalance: number | null;
  closingAsOf: string | null;
  credits: number;
  debits: number;
  net: number;
  transactions: number;
  unreconciled: number;
  internalTransfers: number;
  missingStatement: boolean;
  unfinalizedStatements: number;
}

export interface AccountsSection {
  items: AccountMonth[];
  missingStatements: number;
  unreconciled: number;
}

export interface PartyAmount {
  name: string;
  amount: number;
  count: number;
  share: number;
}

export interface RevenueSection {
  invoices: { count: number; creditNotes: number; net: number; vat: number; total: number };
  collected: number;
  topClientsInvoiced: PartyAmount[];
  topClientsCollected: PartyAmount[];
  concentration: { name: string; share: number } | null;
  otherCurrencies: MoneyByCurrency[];
}

export interface CategoryRow {
  code: string;
  label: string;
  amount: number;
  previous: number;
  average3m: number;
  share: number;
}

export type SubscriptionMonthStatus = 'paid' | 'missing' | 'amount_differs' | 'not_due';

export interface SubscriptionRow {
  id: string;
  name: string;
  frequency: string;
  expectedAmount: number | null;
  /** Absent in snapshots closed before the period refactor. */
  expectedForPeriod?: number | null;
  expectedCurrency: string;
  paidAmount: number;
  paidCurrency: string;
  payments: number;
  status: SubscriptionMonthStatus;
}

export interface ExpensesSection {
  total: number;
  categories: CategoryRow[];
  topSuppliers: PartyAmount[];
  subscriptions: SubscriptionRow[];
  subscriptionsTotal: number;
  subscriptionsRunRateMonthly?: number;
  subscriptionsRunRateAnnual?: number;
}

export type AgingBucketKey = 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90plus';

export interface AgingBucket {
  key: AgingBucketKey;
  count: number;
  amount: number;
}

export interface OpenInvoiceRow {
  id: string;
  source: 'efactura' | 'invoice';
  invoiceNumber: string;
  party: string | null;
  amount: number;
  currency: string;
  issueDate: string | null;
  dueDate: string | null;
  daysOverdue: number;
}

export interface OpenInvoicesSection {
  total: number;
  count: number;
  buckets: AgingBucket[];
  topParty: PartyAmount | null;
  items: OpenInvoiceRow[];
  otherCurrencies: MoneyByCurrency[];
}

export interface PayablesSection extends OpenInvoicesSection {
  scheduledNextMonth: {
    payments: { count: number; total: number };
    receipts: { count: number; total: number };
  };
}

export interface VatPeriod {
  label: string;
  collected: number;
  deductible: number;
  balance: number;
}

export interface VatSection {
  applicable: boolean;
  /** The reported period; `month` is the historical alias of the same figures. */
  period?: VatPeriod;
  month: VatPeriod;
  quarter: VatPeriod;
  /** Only for a yearly report. */
  quarters?: VatPeriod[];
}

export type DataQualityWarning =
  | 'no_statements'
  | 'statements_unfinalized'
  | 'unmatched_lines'
  | 'ledger_empty'
  | 'accounts_without_statement';

export interface DataQualitySection {
  transactions: {
    total: number;
    reconciled: number;
    unmatched: number;
    unmatchedAmount: number;
    ignored: number;
  };
  statements: { total: number; finalized: number; unfinalized: number };
  accountsWithoutStatement: number;
  receivedInvoicesUnpaid: { count: number; total: number };
  ledgerRows: number;
  warnings: DataQualityWarning[];
}

export interface ProjectRow {
  projectId: string;
  name: string;
  code: string | null;
  revenue: number;
  expenses: number;
  margin: number;
  marginPct: number | null;
  // Labour + budget columns; absent in snapshots closed before they existed.
  hours?: number;
  laborCost?: number;
  hoursWithoutRate?: number;
  marginWithLabor?: number;
  marginWithLaborPct?: number | null;
  budget?: number | null;
  budgetCurrency?: string;
  budgetUsedPct?: number | null;
  isBarter?: boolean;
}

export type ProjectsWarning = 'labor_estimated' | 'hours_without_rate' | 'budget_other_currency';

export interface ProjectsSection {
  rows: ProjectRow[];
  unallocated: { revenue: number; expenses: number };
  unallocatedExpensesShare: number | null;
  laborCostTotal?: number;
  hoursTotal?: number;
  hoursWithoutRateTotal?: number;
  losingProjects?: number;
  warnings?: ProjectsWarning[];
}

// --- Oameni -------------------------------------------------------------------

export interface PersonCostRow {
  personId: string;
  name: string;
  departmentName: string | null;
  totalCost: number;
  grossSalary: number;
  netSalary: number;
  employeeContributions: number;
  incomeTax: number;
  employerCam: number;
  hours: number;
  costPerHour: number | null;
  leaveDays: number;
  monthsWithPayroll: number;
  share: number;
}

export type PeopleWarning =
  | 'payroll_missing'
  | 'payroll_partial'
  | 'payroll_period_rounded'
  | 'ledger_payroll_mismatch'
  | 'hours_without_payroll';

export interface PeopleSection {
  totalCost: number;
  breakdown: {
    gross: number;
    contributions: number;
    incomeTax: number;
    cam: number;
    net: number;
  };
  previousTotalCost: number;
  rows: PersonCostRow[];
  byDepartment: PartyAmount[];
  headcount: number;
  monthsCovered: number;
  shareOfInflows: number | null;
  shareOfOutflows: number | null;
  revenuePerPerson: number | null;
  returnPerLeu: number | null;
  hoursTotal: number;
  hoursUnbooked: number;
  ledgerPaid: number;
  ledgerDividends: number;
  ledgerVsPayroll: number;
  trend: Array<{ period: string; totalCost: number; headcount: number }>;
  warnings: PeopleWarning[];
}

// --- Clienți ------------------------------------------------------------------

export interface ClientRow {
  clientId: string | null;
  key: string;
  name: string;
  invoiced: number;
  collected: number;
  open: number;
  overdue: number;
  invoiceCount: number;
  dso: number | null;
  firstInvoiceAt: string | null;
  lastInvoiceAt: string | null;
  isNew: boolean;
  share: number;
}

export interface DormantClient {
  key: string;
  name: string;
  lastInvoiceAt: string;
  lastAmount: number;
  daysSince: number;
}

export interface ClientsSection {
  rows: ClientRow[];
  invoicedTotal: number;
  collectedTotal: number;
  concentration: { top1: number; top3: number; hhi: number } | null;
  newClients: ClientRow[];
  dormant: DormantClient[];
  dso: number | null;
  dsoPrevious: number | null;
  overdueByClient: PartyAmount[];
  trend: Array<{ period: string; invoiced: number; collected: number; clients: number }>;
  otherCurrencies: MoneyByCurrency[];
}

export interface TrendMonth {
  period: string;
  year: number;
  month: number;
  revenue: number;
  expenses: number;
  net: number;
  cumulativeNet: number;
  balance: number | null;
}

export interface TrendsSection {
  months: TrendMonth[];
  /** Granularity of the buckets; absent in pre-refactor snapshots (months). */
  bucketKind?: ReportPeriodKind;
  averageExpenses3m: number;
  runwayMonths: number | null;
}

export interface MonthlyReport {
  year: number;
  month: number;
  period: string;
  /** Absent in snapshots closed before the period refactor (always a month). */
  periodInfo?: ReportPeriodInfo;
  reportVersion?: number;
  generatedAt: string;
  companyIds: string[] | null;
  summary: ReportSummary;
  accounts: AccountsSection;
  revenue: RevenueSection;
  expenses: ExpensesSection;
  receivables: OpenInvoicesSection;
  payables: PayablesSection;
  vat: VatSection;
  dataQuality: DataQualitySection;
  projects: ProjectsSection;
  trends: TrendsSection;
  /** Sections added after the first release; absent in older snapshots. */
  people?: PeopleSection;
  clients?: ClientsSection;
}

export interface ClosingInfo {
  closed: boolean;
  closedAt: string | null;
  closedBy: { id: string; name: string } | null;
}

export interface MonthlyReportResponse {
  report: MonthlyReport;
  closing: ClosingInfo;
  fromSnapshot: boolean;
}

export interface ClosingListItem {
  year: number;
  month: number;
  closedAt: string;
}

// --- Fetchers -----------------------------------------------------------------

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) search.set(k, v);
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

function companyIdsCsv(companyIds?: string[]): string | undefined {
  return companyIds && companyIds.length > 0 ? companyIds.join(',') : undefined;
}

/** Raportul unei perioade (lună, trimestru, an sau interval liber). */
export async function getFinancialReport(
  period: ReportPeriodState,
  companyIds?: string[],
  live = false,
): Promise<MonthlyReportResponse> {
  const qs = query({
    ...periodParams(period),
    companyIds: companyIdsCsv(companyIds),
    live: live ? 'true' : undefined,
  });
  const res = await apiFetch<Envelope<MonthlyReportResponse>>(`/api/financials/report${qs}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getMonthlyReport(
  year: number,
  month: number,
  companyIds?: string[],
  live = false,
): Promise<MonthlyReportResponse> {
  const qs = query({ companyIds: companyIdsCsv(companyIds), live: live ? 'true' : undefined });
  const res = await apiFetch<Envelope<MonthlyReportResponse>>(
    `/api/financials/monthly-report/${year}/${month}${qs}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function listMonthlyReportClosings(companyIds?: string[]): Promise<ClosingListItem[]> {
  const qs = query({ companyIds: companyIdsCsv(companyIds) });
  const res = await apiFetch<Envelope<ClosingListItem[]>>(
    `/api/financials/monthly-report/closings${qs}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function closeMonthlyReport(
  year: number,
  month: number,
  companyIds?: string[],
): Promise<MonthlyReportResponse> {
  const qs = query({ companyIds: companyIdsCsv(companyIds) });
  const res = await apiFetch<Envelope<MonthlyReportResponse>>(
    `/api/financials/monthly-report/${year}/${month}/close${qs}`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

export async function reopenMonthlyReport(
  year: number,
  month: number,
  companyIds?: string[],
): Promise<MonthlyReportResponse> {
  const qs = query({ companyIds: companyIdsCsv(companyIds) });
  const res = await apiFetch<Envelope<MonthlyReportResponse>>(
    `/api/financials/monthly-report/${year}/${month}/close${qs}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

/**
 * Descarcă un export al raportului. `fetch` direct (nu `apiFetch`, care
 * presupune JSON) → blob → click pe un anchor, exact ca `downloadHoursExport`.
 */
async function downloadReport(
  format: 'pdf' | 'xlsx',
  period: ReportPeriodState,
  companyIds?: string[],
): Promise<void> {
  const apiUrl = apiBase();
  const qs = query({ ...periodParams(period), companyIds: companyIdsCsv(companyIds) });
  const res = await fetch(`${apiUrl}/api/financials/report/${format}${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] ?? `raport-financiar-${periodKey(period)}.${format}`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function downloadReportPdf(period: ReportPeriodState, companyIds?: string[]): Promise<void> {
  return downloadReport('pdf', period, companyIds);
}

export function downloadReportXlsx(
  period: ReportPeriodState,
  companyIds?: string[],
): Promise<void> {
  return downloadReport('xlsx', period, companyIds);
}

/** Download the month's PDF (same flow as exportFinancials: blob → anchor click). */
export async function downloadMonthlyReportPdf(
  year: number,
  month: number,
  companyIds?: string[],
): Promise<void> {
  const apiUrl = apiBase();
  const qs = query({ companyIds: companyIdsCsv(companyIds) });
  const res = await fetch(`${apiUrl}/api/financials/monthly-report/${year}/${month}/pdf${qs}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] ?? `raport-lunar-${year}-${String(month).padStart(2, '0')}.pdf`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
